// ═══════════════════════════════════════════════════════════════
// Map Engine — Public API
//
// Usage:
//   Const engine = new MapEngine(canvas, { mode: 'edit' });
//   Engine.load(layout);
//   Engine.on('table:select', (t) => { ... });
//   Engine.on('zoom', (pct) => { ... });
//   // later:
//   Engine.destroy();
// ═══════════════════════════════════════════════════════════════

import { Camera } from "./camera";
import { findZoneAt } from "./hit-test";
import { InputHandler } from "./input";
import type { InputHost } from "./input";
import { Renderer } from "./renderer";
import type { RenderState } from "./renderer";
import type {
  MapConnection,
  MapEngineOptions,
  MapEventHandler,
  MapEventMap,
  MapLayout,
  MapMode,
  MapTable,
  MapZone,
  TableShape,
} from "./types";

// Re-export everything consumers might need
export { Camera } from "./camera";
export { STATUS, STATUS_KEYS, ICONS } from "./constants";
export type {
  MapTable,
  MapZone,
  MapFixedZone,
  MapConnection,
  MapLayout,
  MapMode,
  MapEngineOptions,
  MapEventMap,
  TableShape,
  TableStatus,
  TableIcon,
  StatusStyle,
} from "./types";

// ── MapEngine ─────────────────────────────────────────────────

export class MapEngine implements InputHost {
  // Public readonly refs
  readonly canvas: HTMLCanvasElement;
  readonly camera: Camera;

  // Layout data (mutable by the engine & input handler)
  tables: MapTable[] = [];
  zones: MapZone[] = [];
  private _fixedZones: MapLayout["fixedZones"] = [];
  private _connections: MapConnection[] = [];

  // Visual state
  selectedTableId: string | null = null;
  selectedZoneId: string | null = null;
  hoveredTableId: string | null = null;
  highlightedTableIds: string[] = [];
  // Tables to render with a soft colored glow behind them — used to mark
  // every member of a connected (joinable) group.
  glowingTableIds: string[] = [];
  connectMode = false;
  connectFirstId: string | null = null;
  mapLocked = false;
  /** Optional background override (set by the consumer to signal
   * time-travel / preview mode). ``null`` = use the default theme bg. */
  bgOverride: string | null = null;

  // Internal modules
  private renderer: Renderer;
  private input: InputHandler;
  private mode: MapMode;
  private nextZoneId = 100;
  private startTime = Date.now();
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private worldBounds: { minX: number; minY: number; maxX: number; maxY: number } | null;

  // Event listeners
  private listeners = new Map<string, Set<Function>>();

  // RAF-based render batching
  private dirty = false;
  private rafId: number | null = null;

  constructor(canvas: HTMLCanvasElement, opts: MapEngineOptions = {}) {
    this.canvas = canvas;
    this.mode = opts.mode ?? "view";
    this.camera = new Camera(
      opts.initialScale ?? 0.85,
      opts.initialOffsetX ?? 50,
      opts.initialOffsetY ?? 30,
    );
    this.worldBounds = opts.worldBounds ?? null;
    this.renderer = new Renderer(canvas);
    this.input = new InputHandler(this);

    // Auto-resize — forceRender to avoid blank frame between clear and redraw
    this.resizeObserver = new ResizeObserver(() => {
      this.renderer.resize();
      this.forceRender();
    });
    this.resizeObserver.observe(canvas);

    // Refresh timers every minute
    this.timerInterval = setInterval(() => this.requestRender(), 60_000);

    this.requestRender();
  }

  // ── Lifecycle ───────────────────────────────────────────────

  destroy() {
    this.input.destroy();
    this.resizeObserver?.disconnect();
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    this.listeners.clear();
  }

  // ── Mode ────────────────────────────────────────────────────

  get isEditMode(): boolean {
    return this.mode === "edit";
  }

  setMode(mode: MapMode) {
    this.mode = mode;
    this.emit("mode:change", mode);
    this.requestRender();
  }

  getMode(): MapMode {
    return this.mode;
  }

  setMapLocked(locked: boolean) {
    this.mapLocked = locked;
  }

  // ── Data I/O ────────────────────────────────────────────────

  load(layout: Partial<MapLayout>) {
    if (layout.tables) {
      this.tables = layout.tables;
    }
    if (layout.zones) {
      this.zones = layout.zones;
    }
    if (layout.fixedZones) {
      this._fixedZones = layout.fixedZones;
    }
    if (layout.connections) {
      this._connections = layout.connections;
    }

    // Compute next IDs
    if (this.zones.length > 0) {
      this.nextZoneId =
        Math.max(...this.zones.map((z) => parseInt(z.id.replace("z", "")) || 0), 99) + 1;
    }

    this.selectedTableId = null;
    this.selectedZoneId = null;
    this.requestRender();
  }

  serialize(): MapLayout {
    // ``structuredClone`` ships in Safari 15.4+; iPads on iOS 15.0–15.3 break
    // here. The map layout is plain JSON-serialisable data, so the JSON
    // round-trip is an equivalent deep clone with broader support.
    const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
    return {
      connections: clone(this._connections),
      fixedZones: clone(this._fixedZones),
      tables: clone(this.tables),
      zones: clone(this.zones),
    };
  }

  // ── Table CRUD ──────────────────────────────────────────────

  addTable(shape: TableShape): MapTable {
    const center = this.camera.screenToWorld(this.renderer.width / 2, this.renderer.height / 2);
    const id = crypto.randomUUID();
    const { x, y } = this.clampToWorld(center.x - 35, center.y - 35, 70, 70);
    const table: MapTable = {
      capacity: 4,
      h: 70,
      icon: "none",
      id,
      label: `Nueva`,
      shape,
      status: "available",
      timer: 0,
      noShowTimer: -1,
      w: 70,
      x,
      y,
      zone: null,
    };

    // Auto-assign to zone if center lands inside one
    const zone = findZoneAt(this.zones, table.x + 35, table.y + 35);
    if (zone) {
      table.zone = zone.id;
    }

    this.tables.push(table);
    this.selectedTableId = id;
    this.requestRender();
    return table;
  }

  removeTable(id: string) {
    this._connections = this._connections.filter(([a, b]) => a !== id && b !== id);
    this.tables = this.tables.filter((t) => t.id !== id);
    if (this.selectedTableId === id) {
      this.selectedTableId = null;
    }
    this.requestRender();
  }

  updateTable(id: string, patch: Partial<MapTable>) {
    const table = this.tables.find((t) => t.id === id);
    if (!table) {
      return;
    }
    Object.assign(table, patch);
    this.requestRender();
  }

  getTable(id: string): MapTable | undefined {
    return this.tables.find((t) => t.id === id);
  }

  // ── Zone CRUD ───────────────────────────────────────────────

  addZone(): MapZone {
    const center = this.camera.screenToWorld(this.renderer.width / 2, this.renderer.height / 2);
    const id = `z${this.nextZoneId++}`;
    const { x, y } = this.clampToWorld(center.x - 150, center.y - 100, 300, 200);
    const zone: MapZone = {
      border: "#818cf8",
      color: "#e0e7ff",
      h: 200,
      id,
      label: "NUEVA ZONA",
      labelColor: "#4338ca",
      // Explicit `false` so the renderer applies the legacy alpha
      // attenuation (matching the non-brand palette below); flipping
      // to a brand zone happens when the operator renames it from
      // the side panel (MapCanvas zone-save handler).
      preMixedColors: false,
      w: 300,
      x,
      y,
    };
    this.zones.push(zone);
    this.selectedZoneId = id;
    this.requestRender();
    return zone;
  }

  removeZone(id: string) {
    for (const t of this.tables) {
      if (t.zone === id) {
        t.zone = null;
      }
    }
    this.zones = this.zones.filter((z) => z.id !== id);
    if (this.selectedZoneId === id) {
      this.selectedZoneId = null;
    }
    this.requestRender();
  }

  updateZone(id: string, patch: Partial<MapZone>) {
    const zone = this.zones.find((z) => z.id === id);
    if (!zone) {
      return;
    }
    Object.assign(zone, patch);
    this.requestRender();
  }

  getZone(id: string): MapZone | undefined {
    return this.zones.find((z) => z.id === id);
  }

  // ── Connections ─────────────────────────────────────────────

  /** Toggle a connection; returns true if the connection now exists */
  toggleConnection(idA: string, idB: string): boolean {
    const idx = this._connections.findIndex(
      ([a, b]) => (a === idA && b === idB) || (b === idA && a === idB),
    );
    const connected = idx === -1;

    if (connected) {
      this._connections.push([idA, idB]);
    } else {
      this._connections.splice(idx, 1);
    }

    this.emit("connect:toggle", { connected, idA, idB });
    this.requestRender();
    return connected;
  }

  disconnectTable(id: string) {
    this._connections = this._connections.filter(([a, b]) => a !== id && b !== id);
    this.requestRender();
  }

  getConnections(): MapConnection[] {
    return this._connections;
  }

  setConnections(connections: MapConnection[]) {
    this._connections = connections;
  }

  // ── Connect mode ────────────────────────────────────────────

  setConnectMode(enabled: boolean) {
    this.connectMode = enabled;
    this.connectFirstId = null;
    this.requestRender();
  }

  // ── Camera shortcuts ────────────────────────────────────────

  resetCamera() {
    this.camera.reset();
    this.emit("zoom", this.camera.zoomPercent);
    this.requestRender();
  }

  /** Pan and zoom so the given table IDs are centered and visible.
   *  maxScale prevents zooming in beyond a given level (e.g. 1.0 = 100%). */
  focusTables(ids: string[], padding = 80, rightInset = 0, maxScale?: number) {
    const targets = this.tables.filter((t) => ids.includes(t.id));
    if (targets.length === 0) return;

    const minX = Math.min(...targets.map((t) => t.x));
    const minY = Math.min(...targets.map((t) => t.y));
    const maxX = Math.max(...targets.map((t) => t.x + t.w));
    const maxY = Math.max(...targets.map((t) => t.y + t.h));

    this.camera.fitToBounds(
      { maxX, maxY, minX, minY },
      this.renderer.width - rightInset,
      this.renderer.height,
      padding,
    );

    if (maxScale !== undefined && this.camera.scale > maxScale) {
      this.camera.scale = maxScale;
      // Re-center at the capped scale
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      this.camera.offsetX = (this.renderer.width - rightInset) / 2 - cx * maxScale;
      this.camera.offsetY = this.renderer.height / 2 - cy * maxScale;
    }

    this.emit("zoom", this.camera.zoomPercent);
    this.requestRender();
  }

  /** Fit camera so all zones and tables are visible with padding.
   *  rightInset shrinks the usable viewport from the right (e.g. sidebar).
   *  Items whose center lies outside worldBounds are ignored: a single
   *  stale item with bogus coordinates would otherwise blow the scale past
   *  MIN_SCALE and push every real item off-screen. */
  fitToContent(padding = 80, rightInset = 0, topInset = 0) {
    const items = [...this.zones, ...this.tables].filter((it) => this.isWithinWorld(it));
    if (items.length === 0) {
      return;
    }

    // Force a fresh measurement before computing the fit. Without this, an
    // orientation change can leave `renderer.width/height` one ResizeObserver
    // tick behind the actual canvas size, which throws off centering.
    this.renderer.resize();

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const item of items) {
      minX = Math.min(minX, item.x);
      minY = Math.min(minY, item.y);
      maxX = Math.max(maxX, item.x + item.w);
      maxY = Math.max(maxY, item.y + item.h);
    }

    this.camera.fitToBounds(
      { maxX, maxY, minX, minY },
      this.renderer.width - rightInset,
      this.renderer.height,
      padding,
      topInset,
    );
    this.emit("zoom", this.camera.zoomPercent);
    this.requestRender();
  }

  // ── World bounds helpers ────────────────────────────────────

  /** Clamp a rect's top-left so the rect fits inside worldBounds. Rounds to
   *  integers for consistency with snap grid math. */
  clampToWorld(x: number, y: number, w: number, h: number) {
    const wb = this.worldBounds;
    if (!wb) {
      return { x: Math.round(x), y: Math.round(y) };
    }
    const clampedX = Math.min(Math.max(x, wb.minX), Math.max(wb.minX, wb.maxX - w));
    const clampedY = Math.min(Math.max(y, wb.minY), Math.max(wb.minY, wb.maxY - h));
    return { x: Math.round(clampedX), y: Math.round(clampedY) };
  }

  /** Keep items whose center sits inside worldBounds. When no bounds are
   *  configured, all items pass through. */
  private isWithinWorld(item: { x: number; y: number; w: number; h: number }): boolean {
    const wb = this.worldBounds;
    if (!wb) {
      return true;
    }
    const cx = item.x + item.w / 2;
    const cy = item.y + item.h / 2;
    return cx >= wb.minX && cx <= wb.maxX && cy >= wb.minY && cy <= wb.maxY;
  }

  // ── InputHost contract ──────────────────────────────────────

  bringTableToFront(table: MapTable) {
    this.tables = this.tables.filter((t) => t.id !== table.id);
    this.tables.push(table);
  }

  getZoneTables(zoneId: string): MapTable[] {
    return this.tables.filter((t) => t.zone === zoneId);
  }

  notifyTableDrop(table: MapTable, fromZone: string | null) {
    this.emit("table:drop", {
      fromZone,
      table,
      toZone: table.zone,
    });
  }

  notifyZoneResize(zone: MapZone) {
    this.emit("zone:resize", zone);
  }

  // ── Render scheduling ───────────────────────────────────────

  requestRender() {
    if (this.dirty) {
      return;
    }
    this.dirty = true;
    this.rafId = requestAnimationFrame(() => {
      this.dirty = false;
      this.rafId = null;
      const state: RenderState = {
        bgOverride: this.bgOverride,
        connectFirstId: this.connectFirstId,
        connectMode: this.connectMode,
        glowingTableIds: this.glowingTableIds,
        highlightedTableIds: this.highlightedTableIds,
        hoveredTableId: this.hoveredTableId,
        isEditMode: this.isEditMode,
        selectedTableId: this.selectedTableId,
        selectedZoneId: this.selectedZoneId,
        startTime: this.startTime,
      };
      this.renderer.render(
        this.camera,
        {
          connections: this._connections,
          fixedZones: this._fixedZones,
          tables: this.tables,
          zones: this.zones,
        },
        state,
      );
      // Continue animation loop if scales are still interpolating
      if (this.renderer.needsAnimationFrame) {
        this.requestRender();
      }
    });
  }

  /** Force an immediate (synchronous) render */
  forceRender() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.dirty = false;
    const state: RenderState = {
      connectFirstId: this.connectFirstId,
      connectMode: this.connectMode,
      glowingTableIds: this.glowingTableIds,
      highlightedTableIds: this.highlightedTableIds,
      hoveredTableId: this.hoveredTableId,
      isEditMode: this.isEditMode,
      selectedTableId: this.selectedTableId,
      selectedZoneId: this.selectedZoneId,
      startTime: this.startTime,
    };
    this.renderer.render(
      this.camera,
      {
        connections: this._connections,
        fixedZones: this._fixedZones,
        tables: this.tables,
        zones: this.zones,
      },
      state,
    );
  }

  // ── Event emitter ───────────────────────────────────────────

  on<K extends keyof MapEventMap>(event: K, handler: MapEventHandler<K>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)?.add(handler);
    return () => this.off(event, handler);
  }

  off<K extends keyof MapEventMap>(event: K, handler: MapEventHandler<K>) {
    this.listeners.get(event)?.delete(handler);
  }

  emit(event: string, data?: unknown) {
    const set = this.listeners.get(event);
    if (set) {
      for (const fn of set) fn(data);
    }
  }
}
